import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Send, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Event } from '@/lib/types';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { submitEventFeedback, EventIssueType } from '@/services/feedbackService';
import { ScrollArea } from '@/components/ui/scroll-area';

const feedbackFormSchema = z.object({
  eventId: z.string(),
  issueType: z.enum(['incorrect_date', 'incorrect_venue', 'broken_link', 'missing_info', 'other'] as const),
  description: z.string().min(10, {
    message: 'A leírásnak legalább 10 karakter hosszúnak kell lennie.',
  }),
  email: z.string().email({
    message: 'Érvényes e-mail címet adjon meg.',
  }).optional().or(z.literal('')),
});

type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

const issueTypeLabels = {
  incorrect_date: 'Hibás dátum/időpont',
  incorrect_venue: 'Hibás helyszín adatok',
  broken_link: 'Nem működő link',
  missing_info: 'Hiányzó információk',
  other: 'Egyéb hiba',
};

interface EventFeedbackFormProps {
  event: Event;
  isOpen: boolean;
  onClose: () => void;
}

const EventFeedbackForm = ({ event, isOpen, onClose }: EventFeedbackFormProps) => {
  const { toast } = useToast();
  
  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: {
      eventId: event?.id || '',
      issueType: 'other',
      description: '',
      email: '',
    },
  });

  const onSubmit = async (values: FeedbackFormValues) => {
    try {
      console.log('Feedback submitted:', values);
      await submitEventFeedback({
        ...values,
        issueType: values.issueType as EventIssueType
      });
      
      toast({
        title: 'Köszönjük a visszajelzést!',
        description: 'A problémát rögzítettük, és hamarosan foglalkozunk vele.',
      });
      
      form.reset();
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Hiba történt!',
        description: 'Nem sikerült elküldeni a visszajelzést. Kérjük, próbálja újra később.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 flex flex-col h-[calc(100vh-40px)] max-h-[600px] sm:h-auto">
        <DialogHeader className="p-4 sm:p-6 border-b">
          <DialogTitle className="text-xl">Probléma jelentése</DialogTitle>
          <DialogDescription>
            Kérjük, jelezze számunkra, ha hibát talált a következő eseménnyel kapcsolatban:
            <span className="font-semibold block mt-1">{event?.name}</span>
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 overflow-auto">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} id="feedback-form" className="p-4 sm:p-6 space-y-4">
              <FormField
                control={form.control}
                name="issueType"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel className="font-medium">Mi a probléma típusa?</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="space-y-1"
                      >
                        {Object.entries(issueTypeLabels).map(([value, label]) => (
                          <FormItem key={value} className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={value} />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer text-sm">
                              {label}
                            </FormLabel>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium">Részletes leírás</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Kérjük, írja le részletesen a talált problémát..."
                        className="min-h-[120px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Adjon meg minden olyan részletet, ami segíthet a probléma megoldásában.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium">E-mail cím (nem kötelező)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="pelda@email.hu"
                        {...field}
                        type="email"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Adja meg e-mail címét, ha szeretne értesítést kapni a probléma megoldásáról.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </ScrollArea>
        
        <DialogFooter className="p-4 sm:p-6 border-t">
          <div className="flex w-full flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" type="button" onClick={onClose} className="w-full sm:w-auto flex-1 sm:flex-initial">
              <X className="mr-2 h-4 w-4" />
              Mégsem
            </Button>
            <Button type="submit" form="feedback-form" className="w-full sm:w-auto flex-1 sm:flex-initial">
              <Send className="mr-2 h-4 w-4" />
              Küldés
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EventFeedbackForm;
